import { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { toast } from 'react-toastify';
import { FaFileAlt, FaUpload, FaTrash } from 'react-icons/fa';
import { 
  createMeetingNoteDocument, 
  deleteMeetingNoteDocument 
} from '../../api/documents';
import { DocumentUploadModal } from '../DocumentUploadModal';

interface MeetingNotesDocumentAttachmentProps {
  projectId: string;
  recordId: string;
  documentUrl?: string;
  fileName?: string;
  onUpdate?: () => Promise<void>;
}

const MeetingNotesDocumentAttachmentComponent = ({ 
  projectId, 
  recordId,
  documentUrl,
  fileName,
  onUpdate
}: MeetingNotesDocumentAttachmentProps) => {
  const { keycloak: { token } } = useKeycloak();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [localDocumentUrl, setLocalDocumentUrl] = useState<string | undefined>(documentUrl);
  const [localFileName, setLocalFileName] = useState<string | undefined>(fileName);

  // Update local state when props change
  useEffect(() => {
    setLocalDocumentUrl(documentUrl);
    setLocalFileName(fileName);
  }, [documentUrl, fileName]);

  const handleUploadFromModal = async (file: File) => {
    if (!token) return;

    try {
      console.log('Uploading meeting note document:', file.name, 'Size:', (file.size / 1024 / 1024).toFixed(2), 'MB');
      
      const response = await createMeetingNoteDocument(token, projectId, recordId, file);
      console.log('Meeting note upload response:', response);
      
      // Set local state immediately for instant UI feedback
      setLocalFileName(file.name);
      setLocalDocumentUrl(response.url || (response as any).document_url);
      
      // Trigger parent component update to refresh meeting notes list
      if (onUpdate) {
        setTimeout(async () => {
          await onUpdate();
        }, 500);
      }
      
      toast.success(`Document "${file.name}" uploaded successfully`);
      return response;
    } catch (error) {
      console.error('Failed to upload meeting note document:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // Handle specific HTTP errors with user-friendly messages
      if (errorMessage.includes('413') || errorMessage.includes('Content Too Large')) {
        const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
        toast.error(`File "${file.name}" (${fileSizeMB}MB) is too large. Maximum size is 10MB. Please try a smaller file.`);
      } else if (errorMessage.includes('415') || errorMessage.includes('Unsupported Media Type')) {
        toast.error('File type not supported. Please try a different file format.');
      } else if (errorMessage.includes('401') || errorMessage.includes('403')) {
        toast.error('You do not have permission to upload documents to this meeting note.');
      } else if (errorMessage.includes('404')) {
        toast.error('Meeting note not found. Please refresh the page and try again.');
      } else if (errorMessage.includes('500')) {
        toast.error('Server error occurred. Please try again in a few moments.');
      } else {
        toast.error(`Failed to upload document: ${errorMessage}`);
      }
      
      throw error; // Re-throw to let DocumentUploadModal handle it
    }
  };

  const handleDownload = async () => {
    if (!localDocumentUrl || !token) {
      console.warn('Cannot download: missing document URL or token');
      return;
    }

    try {
      console.log('Downloading meeting note document:', localDocumentUrl);
      
      const response = await fetch(localDocumentUrl, {
        method: 'GET',
        headers: {
          'authorization': 'Bearer ' + token
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const filename = localFileName || 'meeting-note-document';
        
      // Check if file can be opened in browser
      const contentType = response.headers.get('content-type') || '';
      const openableTypes = ['pdf', 'image', 'text'];
      const canOpen = openableTypes.some(type => 
        contentType.toLowerCase().includes(type)
      );

      if (canOpen) {
        // Open in new tab for viewable files
        console.log('Opening file in new tab');
        window.open(url, '_blank');
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
      } else {
        // Force download for other file types
        console.log('Forcing download');
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }

      toast.success('Document downloaded successfully');
      
    } catch (error) {
      console.error('Failed to download document:', error);
      toast.error(`Failed to download document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDelete = async () => {
    if (!token || !projectId || !recordId) return;

    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      console.log(`🗑️ Attempting to delete meeting note document:`, {
        projectId,
        recordId,
        tokenExists: !!token,
        tokenLength: token?.length,
        currentDocumentUrl: localDocumentUrl,
        currentFileName: localFileName
      });
      
      const result = await deleteMeetingNoteDocument(token, projectId, recordId);
      console.log(`🗑️ Delete API result:`, result);
      
      // Clear local state immediately for instant UI feedback
      setLocalDocumentUrl(undefined);
      setLocalFileName(undefined);
      
      toast.success('Document deleted successfully');
      
      // Wait a bit for server-side async deletion to complete (HTTP 202 response)
      // before triggering parent component update
      if (onUpdate) {
        console.log('🗑️ Waiting 5 seconds for server-side deletion to complete...');
        setTimeout(async () => {
          console.log('🗑️ Now refreshing parent component data...');
          
          // Try to verify deletion by checking if document still exists
          try {
            const testUrl = `https://api.finderz.dev/api/v1/projects/${projectId}/meeting-notes/${recordId}/document`;
            const testResponse = await fetch(testUrl, {
              headers: { 'authorization': 'Bearer ' + token }
            });
            console.log('🗑️ Document existence check after deletion:', {
              status: testResponse.status,
              exists: testResponse.ok
            });
          } catch (e) {
            console.log('🗑️ Document existence check failed (might be good - document deleted):', e);
          }
          
          await onUpdate();
        }, 5000); // Increased to 5 seconds
      }
      
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.error(`Failed to delete document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return (
    <div className="flex items-center space-x-1">
      {localDocumentUrl && localFileName ? (
        // Document exists - show name and actions
        <div className="flex items-center space-x-2 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1">
          <button
            onClick={handleDownload}
            className="cursor-pointer flex items-center space-x-1 text-xs text-gray-600 hover:text-gray-800"
            title={localFileName}
          >
            <FaFileAlt className="text-xs" />
            <span className="font-medium" title={localFileName}>
              {(() => {
                const displayName = localFileName;
                return displayName.length > 7 
                  ? `${displayName.substring(0, 7)}...` 
                  : displayName;
              })()}
            </span>
          </button>
          <button
            onClick={handleDelete}
            className="text-xs text-gray-400 hover:text-red-500 p-1"
            title="Delete document"
          >
            <FaTrash />
          </button>
        </div>
      ) : (
        // No document - show upload button
        <div className="flex items-center">
          <button
            onClick={() => setIsUploadModalOpen(true)}
            className="flex items-center space-x-1 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg px-3 py-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
            title="Upload document"
          >
            <FaUpload className="text-xs" />
            <span>Upload</span>
          </button>
        </div>
      )}
      
      {/* Upload Modal */}
      <DocumentUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleUploadFromModal}
        title="Upload Meeting Note Document"
        maxFileSize={10}
        description="Select a document or drag and drop it here"
      />
    </div>
  );
};

export const MeetingNotesDocumentAttachment = (props: MeetingNotesDocumentAttachmentProps) => (
    <MeetingNotesDocumentAttachmentComponent {...props} />
); 